
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode
} from 'react';
import { RiffDnaLLMResult } from '@/components/AxeLab/RiffDna/riffDnaService';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const RIFF_DNA_API = '/api/riff-dna';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface RiffDnaConfig {
  enabled: boolean;
  vibePrompt: string;
  archetype: string;
  lane: 'melodic' | 'harmonic' | 'hybrid';
  rhythm: string;
  notes: string;
  register: 'low' | 'mid' | 'high';
  tempoCharacter: string;
  feel: string;
  deliberateFlaw: string;
  mutation: string;
  sunoStyle: string;
  sunoTags: string[];
  sunoExclude: string;
}

export interface RiffDnaOutput {
  riffDnaBlock: string;
  compactSummary: string;
}

export interface RiffDnaSnapshotEntry {
  id: string;
  name: string | null;
  createdAt: string;
  config: RiffDnaConfig;
  vibePrompt: string;
  archetype: string;
  lane: string;
}

interface RiffDnaContextType {
  config: RiffDnaConfig;
  output: RiffDnaOutput | null;
  generating: boolean;
  error: string | null;
  setEnabled: (enabled: boolean) => void;
  setVibePrompt: (prompt: string) => void;
  updateField: <K extends keyof RiffDnaConfig>(field: K, value: RiffDnaConfig[K]) => void;
  applyLLMResult: (result: RiffDnaLLMResult) => void;
  setGenerating: (generating: boolean) => void;
  setError: (error: string | null) => void;
  clearRiffDna: () => void;
  getRiffDnaBlock: () => string;
  // Snapshot management (DB-backed)
  snapshots: RiffDnaSnapshotEntry[];
  snapshotsLoading: boolean;
  loadSnapshots: () => Promise<void>;
  saveSnapshot: (name?: string) => Promise<RiffDnaSnapshotEntry | null>;
  loadSnapshot: (id: string) => Promise<void>;
  deleteSnapshot: (id: string) => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════
// DEFAULTS
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEY = 'hookhouse_riff_dna';

const defaultConfig: RiffDnaConfig = {
  enabled: false,
  vibePrompt: '',
  archetype: '',
  lane: 'melodic',
  rhythm: '',
  notes: '',
  register: 'low',
  tempoCharacter: 'strut',
  feel: 'straight',
  deliberateFlaw: '',
  mutation: '',
  sunoStyle: '',
  sunoTags: [],
  sunoExclude: ''
};

// ═══════════════════════════════════════════════════════════════
// OUTPUT BUILDERS
// ═══════════════════════════════════════════════════════════════

/** Appends a period if the sentence doesn't already end with one. */
function asSentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/**
 * One condensed natural-language paragraph instead of labeled
 * Archetype/Character/Feel/Deliberate Flaw/Mutation fields. Verified
 * 2026-09-12 (real Claude generations, same seed, before/after diffed):
 * the labeled-field format wasn't extracting more signal than the same
 * content read as prose -- both the "Deliberate Flaw" (wah-on-rests
 * syncopation) and "Mutation" (dropped root/ghost note) details survived
 * being folded into one paragraph, at roughly half the character cost.
 * A fully-stripped version (labels AND content removed, keeping only the
 * arrangement tags) lost both details -- this is NOT that; the substance
 * stays, only the field-label wrapper goes.
 */
function buildRiffDnaBlock(config: RiffDnaConfig): string {
  const lines: string[] = ['[RIFF DNA]'];

  const opening = config.archetype
    ? `${config.archetype} — ${config.sunoStyle}`
    : config.sunoStyle;
  lines.push(asSentence(config.feel ? `${opening}, ${config.feel} feel throughout` : opening));

  if (config.deliberateFlaw) lines.push(asSentence(config.deliberateFlaw));
  if (config.mutation) lines.push(asSentence(config.mutation));
  lines.push('Vibe descriptors only — do NOT interpret as literal notation.');

  lines.push('');
  lines.push('[RIFF ARRANGEMENT]');
  for (const tag of config.sunoTags) {
    lines.push(tag);
  }

  lines.push('');
  lines.push('[RIFF EXCLUDE]');
  lines.push(config.sunoExclude);

  return lines.join('\n');
}

function buildCompactSummary(config: RiffDnaConfig): string {
  const parts = [
    config.archetype,
    config.lane,
    config.register,
    config.feel
  ].filter(Boolean);

  return parts.join(' / ');
}

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

const RiffDnaContext = createContext<RiffDnaContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════

interface RiffDnaProviderProps {
  children?: ReactNode;
}

export function RiffDnaProvider({ children }: RiffDnaProviderProps) {
  const [config, setConfig] = useState<RiffDnaConfig>(defaultConfig);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<RiffDnaSnapshotEntry[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);

  // Load live config from localStorage (live config stays in localStorage)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed) {
          setConfig(prev => ({ ...prev, ...parsed }));
        }
      }
    } catch (e) {
      console.warn('[RiffDNA] Failed to load state:', e);
    }
  }, []);

  // Save live config to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (e) {
      console.warn('[RiffDNA] Failed to save state:', e);
    }
  }, [config]);

  // ─────────────────────────────────────────────────────────────
  // Reactive Output
  // ─────────────────────────────────────────────────────────────

  const output = useMemo<RiffDnaOutput | null>(() => {
    if (!config.enabled) return null;
    if (!config.sunoStyle) return null;

    return {
      riffDnaBlock: buildRiffDnaBlock(config),
      compactSummary: buildCompactSummary(config)
    };
  }, [config]);

  // ─────────────────────────────────────────────────────────────
  // Config Actions
  // ─────────────────────────────────────────────────────────────

  const setEnabled = useCallback((enabled: boolean) => {
    setConfig(prev => ({ ...prev, enabled }));
  }, []);

  const setVibePrompt = useCallback((vibePrompt: string) => {
    setConfig(prev => ({ ...prev, vibePrompt }));
  }, []);

  const updateField = useCallback(<K extends keyof RiffDnaConfig>(field: K, value: RiffDnaConfig[K]) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const applyLLMResult = useCallback((result: RiffDnaLLMResult) => {
    setConfig(prev => ({
      ...prev,
      archetype: result.archetype || prev.archetype,
      lane: (result.lane as RiffDnaConfig['lane']) || prev.lane,
      rhythm: result.rhythm || prev.rhythm,
      notes: result.notes || prev.notes,
      register: (result.register as RiffDnaConfig['register']) || prev.register,
      tempoCharacter: result.tempoCharacter || prev.tempoCharacter,
      feel: result.feel || prev.feel,
      deliberateFlaw: result.deliberateFlaw || prev.deliberateFlaw,
      mutation: result.mutation || prev.mutation,
      sunoStyle: result.sunoStyle || prev.sunoStyle,
      sunoTags: result.sunoTags?.length ? result.sunoTags : prev.sunoTags,
      sunoExclude: result.sunoExclude || prev.sunoExclude
    }));
    setError(null);
  }, []);

  const clearRiffDna = useCallback(() => {
    setConfig(defaultConfig);
    setError(null);
    setGenerating(false);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Output Helpers
  // ─────────────────────────────────────────────────────────────

  const getRiffDnaBlock = useCallback((): string => {
    return output?.riffDnaBlock || '';
  }, [output]);

  // ─────────────────────────────────────────────────────────────
  // Snapshot Management (DB-backed)
  // ─────────────────────────────────────────────────────────────

  const loadSnapshots = useCallback(async () => {
    setSnapshotsLoading(true);
    try {
      const res = await fetchWithCsrf(`${RIFF_DNA_API}/list`);
      if (res.ok) {
        const json = await res.json();
        setSnapshots(json.data || []);
      }
    } catch (e) {
      console.warn('[RiffDNA] Failed to load snapshots:', e);
    } finally {
      setSnapshotsLoading(false);
    }
  }, []);

  const saveSnapshot = useCallback(async (name?: string): Promise<RiffDnaSnapshotEntry | null> => {
    try {
      const res = await fetchWithCsrf(`${RIFF_DNA_API}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || null,
          config,
          vibePrompt: config.vibePrompt,
          archetype: config.archetype,
          lane: config.lane,
        }),
      });
      if (res.ok) {
        const json = await res.json();
        const entry = json.data as RiffDnaSnapshotEntry;
        setSnapshots(prev => [entry, ...prev]);
        return entry;
      }
    } catch (e) {
      console.warn('[RiffDNA] Failed to save snapshot:', e);
    }
    return null;
  }, [config]);

  const loadSnapshot = useCallback(async (id: string) => {
    try {
      const res = await fetchWithCsrf(`${RIFF_DNA_API}/${id}`);
      if (res.ok) {
        const json = await res.json();
        const snapshotConfig = json.data.config as RiffDnaConfig;
        setConfig(prev => ({ ...prev, ...snapshotConfig }));
      }
    } catch (e) {
      console.warn('[RiffDNA] Failed to load snapshot:', e);
    }
  }, []);

  const deleteSnapshot = useCallback(async (id: string) => {
    try {
      await fetchWithCsrf(`${RIFF_DNA_API}/${id}`, { method: 'DELETE' });
      setSnapshots(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      console.warn('[RiffDNA] Failed to delete snapshot:', e);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────

  const contextValue: RiffDnaContextType = {
    config,
    output,
    generating,
    error,
    setEnabled,
    setVibePrompt,
    updateField,
    applyLLMResult,
    setGenerating,
    setError,
    clearRiffDna,
    getRiffDnaBlock,
    snapshots,
    snapshotsLoading,
    loadSnapshots,
    saveSnapshot,
    loadSnapshot,
    deleteSnapshot,
  };

  return (
    <RiffDnaContext.Provider value={contextValue}>
      {children}
    </RiffDnaContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export function useRiffDna(): RiffDnaContextType {
  const context = useContext(RiffDnaContext);
  if (!context) {
    throw new Error('useRiffDna must be used within RiffDnaProvider');
  }
  return context;
}
